import { useTranslation } from "react-i18next";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { activityActionLabel, activityDetail } from "@/lib/activity-actions";
import { useActivity, useMe, useMeDetail } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { roleLabel } from "@/lib/roles";

import { SettingsSection } from "../settings-section";
import { ProfileForm } from "./profile-form";

/**
 * Settings → Account: the signed-in person's account, name and own activity
 * on the dashboard. While a developer views the dashboard as a person, the
 * viewed person's; as a role, only the role, since a role has no account.
 */
export function AccountSettings() {
  const { t } = useTranslation();
  const view = useMe().data?.view_as;
  const roleView = view?.kind === "role" ? view : null;
  const profileQuery = useMeDetail({ enabled: !roleView });
  const activityQuery = useActivity({ enabled: !roleView });

  if (roleView) {
    return (
      <SettingsSection title={t("nav.account")}>
        <Card className="max-w-2xl">
          <CardContent className="space-y-3 pt-6 text-sm">
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2">
              <dt className="text-muted">{t("settings.role")}</dt>
              <dd>{roleLabel(roleView.role)}</dd>
            </dl>
            <p className="text-muted">{t("settings.roleViewNoAccount")}</p>
          </CardContent>
        </Card>
      </SettingsSection>
    );
  }

  const profile = profileQuery.data;
  const activity = activityQuery.data ?? [];
  const isLoading = profileQuery.isLoading;
  const hasError = profileQuery.isError || activityQuery.isError;

  return (
    <SettingsSection title={t("nav.account")}>
      <div className="max-w-2xl space-y-6">
        {isLoading ? (
          <LoadingCard />
        ) : hasError || !profile ? (
          <LoadErrorCard
            message={t("settings.accountLoadError")}
            onRetry={() => {
              void profileQuery.refetch();
              void activityQuery.refetch();
            }}
          />
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle>{t("settings.accountInfo")}</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
                  <dt className="text-muted">{t("settings.email")}</dt>
                  <dd className="break-all">{profile.email}</dd>
                  <dt className="text-muted">{t("settings.role")}</dt>
                  <dd>{roleLabel(profile.role)}</dd>
                  <dt className="text-muted">{t("settings.memberSince")}</dt>
                  <dd>{formatDate(profile.created_at)}</dd>
                </dl>
              </CardContent>
            </Card>

            <ProfileForm
              initialFirstName={profile.first_name ?? ""}
              initialLastName={profile.last_name ?? ""}
            />

            <Card>
              <CardHeader>
                <CardTitle>{t("settings.yourActivity")}</CardTitle>
              </CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <p className="text-muted text-sm">{t("settings.noActivity")}</p>
                ) : (
                  <div className="space-y-2">
                    {activity.map((entry) => (
                      <div
                        key={entry.id}
                        className="border-border flex items-start justify-between border-b pb-2 last:border-0 max-sm:gap-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium" title={entry.action}>
                            {activityActionLabel(entry.action)}
                          </p>
                          {entry.detail && (
                            <p className="text-muted text-xs max-sm:wrap-anywhere">
                              {activityDetail(entry.action, entry.detail)}
                            </p>
                          )}
                        </div>
                        <p className="text-muted text-xs whitespace-nowrap">
                          {formatDateTime(entry.created_at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </SettingsSection>
  );
}
